// src/oura/components/plan/SignatureQuestions.tsx
//
// The contract's two signature questions (ourteam-backend#5333): one the
// business is already named for, and one no rival is named for. Like the
// priority keywords they are a CONTRACT fact, so they live in What was sold and
// are saved with the plan, through the same "What changed, in one line" bar.
//
// Why the fields exist: `signature_incomplete` blocks publishing the schedule.
// The server picks each question from the AI check when the check supports it,
// and for a business no AI answer names yet it never can, so a new client's
// schedule could not be published from any screen and the merchant was told no
// dates at all. The server has always accepted the pair on the contract
// (`tracking.signature`); this is the screen for it.
//
// Presentational: the draft it is given holds the text. It decides nothing
// about saving.

import { cn } from "../../lib/cn";
import { TYPE } from "@oura/ui";
import { SIGNATURE_FIELD_ID, SIGNATURE_MAX_LENGTH, sameSignature, type SignatureDraft } from "./planModel";

const FIELD = cn(
    TYPE.tb,
    "mt-1 block h-11 w-full rounded-xl border-0 bg-gray-50 px-3 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 sm:h-9",
);

const COVERAGE_FIELD_ID = "oura-plan-signature-coverage";
const NOTE_ID = `${SIGNATURE_FIELD_ID}-note`;

export function SignatureQuestions({
    value,
    saved,
    onChange,
    disabled = false,
}: {
    /** The draft's two questions. "" for one left to the AI check. */
    value: SignatureDraft;
    /** What the contract holds now, so the fields can say an edit is unsaved. */
    saved: SignatureDraft;
    onChange: (next: SignatureDraft) => void;
    disabled?: boolean;
}) {
    const changed = !sameSignature(value, saved);
    return (
        // scroll-mb keeps the fields clear of the sticky save bar when a deep
        // link scrolls them into view, as the keywords above do.
        <fieldset className="m-0 min-w-0 scroll-mb-32 border-0 p-0">
            <legend className={cn(TYPE.lab, "p-0")}>Signature questions</legend>
            <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>
                The schedule needs two questions from the AI check. Oura picks both from the check. Type one here only when Oura cannot, for example
                when no AI answer names the business yet.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div className="min-w-0">
                    <label htmlFor={SIGNATURE_FIELD_ID} className={cn(TYPE.tc, "font-medium text-gray-700")}>
                        A question the business is already named for
                    </label>
                    <input
                        id={SIGNATURE_FIELD_ID}
                        value={value.visibility}
                        disabled={disabled}
                        autoComplete="off"
                        maxLength={SIGNATURE_MAX_LENGTH}
                        placeholder="One of the 50, as it is asked"
                        aria-describedby={NOTE_ID}
                        onChange={(e) => onChange({ ...value, visibility: e.target.value })}
                        className={FIELD}
                    />
                </div>
                <div className="min-w-0">
                    <label htmlFor={COVERAGE_FIELD_ID} className={cn(TYPE.tc, "font-medium text-gray-700")}>
                        A question no rival is named for
                    </label>
                    <input
                        id={COVERAGE_FIELD_ID}
                        value={value.coverage}
                        disabled={disabled}
                        autoComplete="off"
                        maxLength={SIGNATURE_MAX_LENGTH}
                        placeholder="One of the 50, as it is asked"
                        aria-describedby={NOTE_ID}
                        onChange={(e) => onChange({ ...value, coverage: e.target.value })}
                        className={FIELD}
                    />
                </div>
            </div>
            <p id={NOTE_ID} className={cn(TYPE.tc, "m-0 mt-1.5 text-gray-500")}>
                Leave a field blank and Oura picks that one.{changed ? " Not saved yet." : ""}
            </p>
        </fieldset>
    );
}
